import { getSingleAttributeValue } from "@stores/gqlModel/getSingleAttributeValue";
import { IAuthService } from '../platform/services/auth/AuthService';
import { IDataClientService } from '../platform/services/data/DataClientService';
import { getCurrentUserIdToken } from "../utils/user";
import { isConflictResponse, ConflictResponse } from './conflictResolution/types';
import { conflictHandlerRegistry } from './conflictResolution/registry';
import { ConflictError, NoConflictHandlerError, MaxRetriesExceededError } from './conflictResolution/errors';
import { conflictLogger } from './conflictResolution/logger';

/**
 * Options for queryGqlApi
 */
export interface QueryGqlApiOptions {
    /**
     * Enable automatic conflict resolution (default: true)
     */
    autoResolveConflicts?: boolean;

    /**
     * Maximum retry attempts for conflict resolution (default: 3)
     */
    maxConflictRetries?: number;
}

/**
 * Execute a GraphQL query/mutation using the data client service.
 * Supports automatic conflict resolution for versioned updates.
 * 
 * @param query - GraphQL query/mutation string
 * @param variables - GraphQL variables
 * @param dataClientService - Service to resolve endpoints
 * @param authService - Service to get authentication token
 * @param endpointKey - Key to identify which endpoint to use (e.g., 'main', 'quiz', 'pgServer')
 * @param method - HTTP method (default: 'POST')
 * @param options - Additional options including conflict resolution settings
 */
export const queryGqlApi = async (
    query: any,
    variables: any,
    dataClientService: IDataClientService,
    authService: IAuthService,
    endpointKey: string,
    method: 'POST' | 'GET' = 'POST',
    options: QueryGqlApiOptions = {}
): Promise<any> => {
    const {
        autoResolveConflicts = true,
        maxConflictRetries = 3
    } = options;

    // Capture original input at the start for conflict resolution
    const originalInput = variables.input;
    
    let attemptNumber = 0;
    let currentVariables = variables;
    let lastConflict: ConflictResponse | null = null;

    while (attemptNumber <= maxConflictRetries) {
        // Execute GraphQL request
        const response = await executeGraphQLRequest(
            query,
            currentVariables,
            dataClientService,
            authService,
            endpointKey,
            method
        );

        // Check for conflict
        if (isConflictResponse(response)) {
            lastConflict = response;
            
            // Log conflict detection
            console.warn(
                `[Conflict Resolution] Detected conflict for ${response.entityType}#${response.entityId} (attempt ${attemptNumber + 1}/${maxConflictRetries + 1})`
            );

            // Check if we should attempt resolution
            if (!autoResolveConflicts || attemptNumber >= maxConflictRetries) {
                const error = attemptNumber >= maxConflictRetries
                    ? new MaxRetriesExceededError(response, maxConflictRetries)
                    : new ConflictError(response, attemptNumber);
                
                conflictLogger.logConflict(response, attemptNumber, false, error.message);
                throw error;
            }

            // Get conflict handler
            const handler = conflictHandlerRegistry.get(response.entityType);
            if (!handler) {
                const error = new NoConflictHandlerError(response);
                conflictLogger.logConflict(response, attemptNumber, false, error.message);
                throw error;
            }

            // Check if handler wants to retry
            if (handler.shouldRetry && !handler.shouldRetry(response, attemptNumber)) {
                const error = new ConflictError(
                    response,
                    attemptNumber,
                    `Handler declined to retry for ${response.entityType}#${response.entityId}`
                );
                conflictLogger.logConflict(response, attemptNumber, false, error.message);
                throw error;
            }

            try {
                // Resolve conflict
                const currentState = response.currentState || await handler.refetch({
                    entityId: response.entityId,
                    dataClientService,
                    authService
                });

                console.log('[conflict] currentState', currentState)
                const mergedChanges = handler.merge({
                    currentState,
                    intendedChanges: originalInput,
                    originalVersion: response.expectedVersion
                });

                // Update variables for retry
                currentVariables = {
                    ...currentVariables,
                    input: {
                        ...mergedChanges,
                        id: response.entityId,
                        version: currentState.version
                    }
                };

                attemptNumber++;
                continue;
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                conflictLogger.logConflict(response, attemptNumber, false, errorMessage);
                throw new ConflictError(
                    response,
                    attemptNumber,
                    `Failed to resolve conflict: ${errorMessage}`
                );
            }
        }

        // Success - log if this was a retry
        if (attemptNumber > 0 && lastConflict) {
            conflictLogger.logConflict(lastConflict, attemptNumber, true);
        }

        return response;
    }

    throw new Error('Conflict resolution exceeded maximum retries');
};

/**
 * Internal function to execute a GraphQL request.
 * Separated for clarity and to avoid duplication.
 */
async function executeGraphQLRequest(
    query: string,
    variables: any,
    dataClientService: IDataClientService,
    authService: IAuthService,
    endpointKey: string,
    method: 'POST' | 'GET'
): Promise<any> {
    const endpoint = dataClientService.getHttpEndpointByKey(endpointKey);
    const token = await getCurrentUserIdToken(authService);
    
    if (!token) {
        throw new Error('token is empty');
    }

    const response = await fetch(endpoint, {
        method: method,
        headers: {
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            query: query,
            variables: variables,
        }),
    });

    const responseBody = await response.json();

    if (!response.ok) {
        throw new Error(responseBody.message);
    }
    
    if (responseBody.errors) {
        throw new Error(JSON.stringify(responseBody.errors));
    }

    return getSingleAttributeValue(responseBody.data);
}
